import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./therapistTreatmentPlan.module.css";
import React, { useEffect, useMemo, useState } from "react";
import { mobileWidth } from "../../utils/constants";
import { getPrintDate } from "../../utils/utils";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  useGetAllOpenTreatmentPlansMutation,
  useUpdateUnSeenAppointmentsProgramQuery,
} from "../../services/api/user.api";
import { useAppSelector } from "../../services/hooks";
import {
  therapistTreatmentPlansSelector,
  userSelector,
} from "../../services/selectors/user.selectors";
import { ITreatmentPlan, TLimits } from "../../types/types";
import Loader from "../loader/loader";
import ListPagination from "../listPagination/listPagination";
import TreatmentDocument from "../document/treatmentDocument";
import FilterButtonTreatmentPlan from "../filterButton/filterButtonTreatmentPlan";
import BackArrowButton from "../backArrowButton/backArrowButton";
import { useNavigate } from "react-router-dom";
import i18n from "../../i18n";
import groupIcon from "../../images/group_users_icon_black.svg";

function TherapistTreatmentPlan() {
  const therapist = useAppSelector(userSelector);
  const treatmentPlans = useAppSelector(therapistTreatmentPlansSelector);

  const mobile = useMediaQuery(mobileWidth);

  useUpdateUnSeenAppointmentsProgramQuery();
  const [getAllTreatmentPlans, { isLoading: isTreatmentPlanLoading }] =
    useGetAllOpenTreatmentPlansMutation();

  const [treatmentDocumentToShow, setTreatmentDocumentToShow] =
    useState<ITreatmentPlan | null>(null);
  const [openTreatmentDocument, setOpenTreatmentDocument] =
    useState<boolean>(false);
  const [treatmentPlansToshow, setTreatmentPlansToshow] = useState<
    ITreatmentPlan[] | null | undefined
  >(treatmentPlans);
  const [treatmentPlanLimits, setTreatmentPlanLimits] = useState<TLimits>({
    start: 1,
    end: 1,
  });

  //limit inquiries to 7
  const limitNumber = 7;
  const isLoading = isTreatmentPlanLoading;

  useEffect(() => {
    if (therapist?.guid) {
      getAllTreatmentPlans({
        companyId: therapist.companyId,
        therapistGuid: therapist.guid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist?.guid]);

  useEffect(() => {
    if (treatmentPlansToshow && treatmentPlansToshow.length > 0) {
      setTreatmentPlanLimits((prev) => ({
        ...prev,
        end:
          limitNumber < treatmentPlansToshow.length
            ? limitNumber
            : treatmentPlansToshow.length,
      }));
    }
  }, [treatmentPlansToshow]);

  useEffect(() => {
    setTreatmentPlansToshow(treatmentPlans);
  }, [treatmentPlans]);

  const showTreatmentDocument = (plan: ITreatmentPlan) => {
    setTreatmentDocumentToShow(plan);
    setOpenTreatmentDocument(true);
  };

  const setFilterTreatmentToShow = (
    treatments: ITreatmentPlan[] | undefined
  ) => {
    setTreatmentPlansToshow(treatments);
  };
  const navigate = useNavigate();
  const lang = i18n.language;

  const dir = useMemo(() => {
    return lang === "he" || lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  return (
    <section className={styles.section} dir={dir} lang={lang}>
      <BackArrowButton
        text={t("BACK_ARROW_PREVIOUS")}
        onClick={() => navigate(-1)}
        position={"left"}
      />
      {treatmentDocumentToShow && (
        <TreatmentDocument
          treatmentPlan={treatmentDocumentToShow}
          openDocument={openTreatmentDocument}
          onClose={() => {
            setTreatmentDocumentToShow(null);
            setOpenTreatmentDocument(false);
          }}
        />
      )}
      <article className={styles.home__section}>
        <h4 className={styles.home__sectionTitle}>{t("MY_TREATMENT_PLANS")}</h4>
        <ul className={styles.inquiries}>
          <li className={`${styles.inquiry} ${styles.inquiryHeader}`}>
            <p className={`${styles.inquiryCell} ${styles.inquiryNumber}`}>
              {mobile ? t("INQUIRY_NUMBER_SHORT") : t("INQUIRY_PLAN_NUMBER")}
            </p>
            <p className={`${styles.inquiryCell} ${styles.inquiryDate}`}>
              {mobile
                ? t("INQUIRY_OPEN_DATE_SHORT")
                : t("DOCUMENTS_PAGE_TABLE_HEADER_ISSUED")}
            </p>
            <p className={`${styles.inquiryCell} ${styles.inquiryName}`}>
              {mobile ? t("MAIN_PAGE_INQUIRY_NAME_SHORT") : t("USERS_NAME")}
            </p>
            <p className={`${styles.inquiryCell} ${styles.inquiryCycle}`}>
              {mobile ? t("INQUIRY_PLAN") : t("MAIN_PAGE_TREATMENT_NAME")}
            </p>
            <p className={`${styles.inquiryCell} ${styles.inquiryContent}`}>
              {mobile
                ? t("MAIN_PAGE_TREATMENT_METHOD_SHORT")
                : t("MAIN_PAGE_TREATMENT_METHOD_FULL")}
            </p>
            <p className={`${styles.inquiryCell}`}>{t("STATUS")}</p>
            <div className={`${styles.inquiryCell} ${styles.inquiryStatus}`}>
              <FilterButtonTreatmentPlan
                documentsToFilter={treatmentPlans}
                setDocumentsToFilter={setFilterTreatmentToShow}
              />
            </div>
          </li>

          {isLoading ? (
            <li className={styles.emptyRow}>
              <div className={styles.text}>
                <Loader />
              </div>
            </li>
          ) : treatmentPlansToshow && treatmentPlansToshow.length > 0 ? (
            treatmentPlansToshow.map((i, index) => {
              const isCancelled =
                !i.appointmentsProgram.statusId ||
                i.appointmentsProgram.statusId === 8;
              return (
                index >= treatmentPlanLimits.start - 1 &&
                index < treatmentPlanLimits.end && (
                  <li
                    key={index}
                    className={`${styles.inquiry} ${
                      index % 2 === 0 && styles.inquiry_colored
                    } 
                      ${isCancelled && styles.inquiry_canceled}`}
                    onClick={() => {
                      i.appointmentsProgram.statusId &&
                        showTreatmentDocument(i);
                    }}
                  >
                    <p
                      className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                    >
                      {i.appointmentsProgram.id}
                    </p>
                    <p
                      className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                    >
                      {`${getPrintDate(
                        new Date(i.appointmentsProgram.createDate)
                      )}`}
                    </p>
                    <p
                      className={`${styles.inquiryCell} ${styles.inquiryName}`}
                    >
                      {i.treatmentGroupName ? (
                        <span className={styles.groupNameContainer}>
                          <img src={groupIcon} alt="group" className={styles.groupIcon} />
                          {i.treatmentGroupName}
                        </span>
                      ) : (
                        i.user?.fullName || ""
                      )}
                    </p>
                    <p
                      className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                    >
                      {i.appointmentsProgram.name || ""}
                    </p>
                    <p
                      className={`${styles.inquiryCell} ${styles.inquiryCategory}`}
                    >
                      {i.mediaName || ""}
                    </p>
                    <p
                      className={`${styles.inquiryCell} ${styles.statusValue}`}
                    >
                      {isCancelled ? t("STATUS_INACTIVE") : t("STATUS_ACTIVE")}
                    </p>
                    <div
                      className={`${styles.inquiryCell} ${styles.inquiryActions}`}
                    >
                      <button
                        className={`button button_white ${styles.btn_take}`}
                      >
                        {t("DETAILS")}
                      </button>
                    </div>
                    {/* <span className={styles.inquiryRRM} />    */}
                  </li>
                )
              );
            })
          ) : (
            <li className={styles.emptyRow}>
              <p className={styles.emptyRowText}>
                {t("NO_TREATMENT_PLANS_FOUND")}
              </p>
            </li>
          )}
        </ul>
      </article>
      {treatmentPlans && treatmentPlans.length > 0 && (
        <ListPagination
          limits={treatmentPlanLimits}
          total={treatmentPlans?.length || 0}
          setLimits={setTreatmentPlanLimits}
          step={limitNumber}
        />
      )}
    </section>
  );
}

export default TherapistTreatmentPlan;
